import { Injectable } from '@angular/core';
import { WebApiService } from './web-api.service';
import { CookieService } from 'ngx-cookie-service';

export type ChatbotRole = 'system' | 'assistant' | 'user';

export interface ChatbotMessage {
  id: string;
  role: ChatbotRole;
  content: string;
  timestamp: Date;
  tone?: 'info' | 'success' | 'warning';
  cta?: 'notifications' | 'products';
  steps?: string[];
  link?: { label: string; route: any[]; queryParams?: Record<string, any> };
}

export interface QuickPrompt {
  id: string;
  label: string;
  prompt: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatbotAiService {
  constructor(private webApiService: WebApiService, private cookieService: CookieService) {}

  private seedMessages: ChatbotMessage[] = [
    {
      id: 'welcome',
      role: 'assistant',
      content:
        'Hi there! I am the MPS Copilot prototype. Ask me about procurement insights, supplier performance, or let me help you draft an RFQ.',
      timestamp: new Date(),
      tone: 'info',
    },
    {
      id: 'hint',
      role: 'assistant',
      content:
        'Try “Summarize my notifications for this week” or “Suggest top RFQ products for US-OOMI”.',
      timestamp: new Date(),
      tone: 'info',
    },
  ];

  private scriptedResponses: Record<string, string> = {
    overview:
      'Dashboard snapshot: 10 RFQs created, 3 orders placed, and 5 POs issued in the past week. Spend is up 2% WoW with Supplier ABC leading fill rate.',
    notifications:
      'You have 3 unread notifications: PO #5678 accepted by Supplier XYZ, RFQ sent to Supplier ABC, and PO #5567 delayed by 2 days.',
    suppliers:
      'Supplier ABC holds a 95% on-time rate and Supplier XYZ is trending upward with faster acknowledgement cycles.',
    rfq:
      'I can shortlist the top 10 products that match your scenario once the AI backend is connected. For now, review the RFQ cart for curated examples.',
    default:
      'I could not reach the AI service right now. Please try again.',
  };

  private quickPrompts: QuickPrompt[] = [
    {
      id: 'overview',
      label: 'Dashboard overview',
      prompt: 'Give me an overview of procurement analytics .',
    },
    {
      id: 'notifications',
      label: 'Recent notifications',
      prompt: 'What are my recent notifications?',
    },
    
  ];

  private quickActionsByRole: Record<string, QuickPrompt[]> = {
    supplier: [
      { id: 'qa-respond-rfq', label: 'Respond to new RFQs', prompt: 'Help me respond to my latest RFQs.' },
      { id: 'qa-update-catalog', label: 'Update product catalog', prompt: 'Show me steps to update my product catalog.' },
      { id: 'qa-performance', label: 'View my performance', prompt: 'Summarize my current performance metrics.' },
      { id: 'qa-inventory', label: 'Review inventory alerts', prompt: 'Do I have any inventory alerts to review?' },
      { id: 'qa-compliance', label: 'Manage compliance', prompt: 'List my pending compliance tasks.' },
    ],
    buyer: [
      { id: 'qa-create-rfq', label: 'Create a new RFQ', prompt: 'Help me create a new RFQ.' },
      { id: 'qa-track-order', label: 'Track an order', prompt: 'Track my latest order status.' },
      { id: 'qa-evaluate-suppliers', label: 'Evaluate suppliers', prompt: 'Evaluate my suppliers performance.' },
      { id: 'qa-analyze-spend', label: 'Analyze spending', prompt: 'Analyze my spending by category.' },
      { id: 'qa-emergency-order', label: 'Emergency order', prompt: 'Start an emergency order.' },
    ],
  };

  getInitialMessages(): ChatbotMessage[] {
    return this.seedMessages.map((message) => ({ ...message }));
  }

  getQuickPrompts(): QuickPrompt[] {
    return this.quickPrompts.map((prompt) => ({ ...prompt }));
  }

  getQuickActions(role?: string): QuickPrompt[] {
    const resolvedRole = (role || this.getUserRole())?.toLowerCase();
    const actions = resolvedRole && this.quickActionsByRole[resolvedRole] ? this.quickActionsByRole[resolvedRole] : [];
    return actions.map((prompt) => ({ ...prompt }));
  }

  generateResponse(promptId?: string): ChatbotMessage {
    const key = promptId && this.scriptedResponses[promptId] ? promptId : 'default';
    return {
      id: `${key}-${Date.now()}`,
      role: 'assistant',
      content: this.scriptedResponses[key],
      timestamp: new Date(),
      tone: key === 'suppliers' ? 'success' : 'info',
    };
  }

  async generateResponseFromApi(promptId?: string, question?: string): Promise<ChatbotMessage> {
    if (promptId === 'overview') {
      try {
        const role = this.getUserRole();
        const apiRes: any = await this.webApiService.postRequest('/ai/dashboard-overview', {
          question: question || 'Give me an overview of my dashboard.',
          role,
        });
        const answer =
          apiRes?.answer ||
          apiRes?.data?.answer ||
          apiRes?.data?.data?.answer ||
          this.scriptedResponses['overview'];
        return {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: answer,
          timestamp: new Date(),
          tone: 'info',
        };
      } catch {
        return this.generateResponse('overview');
      }
    } else if (promptId === 'notifications') {
      try {
        const role = this.getUserRole();
        const apiRes: any = await this.webApiService.postRequest('/ai/notifications-overview', {
          question: question || 'Summarize my recent notifications.',
          role,
        });
        const answer =
          apiRes?.answer ||
          apiRes?.data?.answer ||
          apiRes?.data?.data?.answer ||
          this.scriptedResponses['notifications'];
        return {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: answer,
          timestamp: new Date(),
          tone: 'info',
          cta: 'notifications',
        };
      } catch {
        return this.generateResponse('notifications');
      }
    } else if (promptId === 'qa-performance') {
      try {
        const role = this.getUserRole();
        const apiRes: any = await this.webApiService.postRequest('/ai/performance-overview', {
          question: question || 'Show my performance overview.',
          role,
        });
        const answer =
          apiRes?.answer ||
          apiRes?.data?.answer ||
          apiRes?.data?.data?.answer ||
          'Here is your performance summary.';
        return {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: answer,
          timestamp: new Date(),
          tone: 'info',
        };
      } catch {
        return this.generateResponse('default');
      }
    } else if (promptId === 'qa-create-rfq') {
      const content = 'Here is the fast path to create a new RFQ:';
      const steps = [
        'Go to Product page and add items to cart',
        'Open the cart and click Create RFQ',
        'Complete Line Items',
        'Fill Header tab',
        'Set Payment Terms',
        'Preview, then Send RFQ or Save as draft',
      ];
      return {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        tone: 'info',
        cta: 'products',
        steps,
      };
    } else if (promptId === 'qa-update-catalog') {
      const content =
        'To update your catalog: open Catalog Management, add or edit products, assign categories/attributes, set pricing and availability, then save. You can bulk upload (if enabled) or manage items individually.';
      const userIdStr = this.cookieService.get('userId');
      const companyIdStr = this.cookieService.get('companyId');
      const userId = userIdStr ? Number(userIdStr) : undefined;
      const companyId = companyIdStr ? Number(companyIdStr) : undefined;
      const route =
        userId && companyId
          ? ['/dataManagement/products', userId, companyId]
          : ['/dataManagement/products'];
      return {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        tone: 'info',
        link: { label: 'Go to catalog', route },
      };
    } else if (promptId === 'qa-compliance') {
      const content =
        'Manage compliance and sustainability: review required certifications, ensure supplier type is set to Sustainable, and update catalog items accordingly.';
      return {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        tone: 'info',
        link: {
          label: 'Go to sustainable suppliers',
          route: ['/search/products'],
          queryParams: { supplierType: 1 },
        },
      };
    } else if (promptId === 'qa-track-order') {
      const content = 'Open your invoice list to track order and payment status.';
      return {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        tone: 'info',
        link: { label: 'Go to invoice list', route: ['/operations/invoice-list'] },
      };
    } else if (promptId === 'qa-emergency-order') {
      const content = 'Opening your purchase orders filtered to sent status for urgent handling.';
      return {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        tone: 'info',
        link: {
          label: 'Go to purchase orders',
          route: ['/operations/purchase-order-list'],
          queryParams: { docStatus: 'Sent', selectedStatus: 'Sent', label: 'Order' },
        },
      };
    } else if (promptId === 'qa-analyze-spend') {
      try {
        const role = this.getUserRole();
        const apiRes: any = await this.webApiService.postRequest('/ai/spending-overview', {
          question: question || 'Analyze my spending by category.',
          role,
        });
        const answer =
          apiRes?.answer ||
          apiRes?.data?.answer ||
          apiRes?.data?.data?.answer ||
          'Here is your spending analysis.';
        return {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: answer,
          timestamp: new Date(),
          tone: 'info',
        };
      } catch {
        return this.generateResponse('default');
      }
    } else if (promptId === 'qa-evaluate-suppliers') {
      try {
        const role = this.getUserRole();
        const apiRes: any = await this.webApiService.postRequest('/ai/supplier-evaluation', {
          question: question || 'Evaluate my suppliers and share insights.',
          role,
        });
        const answer =
          apiRes?.answer ||
          apiRes?.data?.answer ||
          apiRes?.data?.data?.answer ||
          'Here is your supplier evaluation.';
        return {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: answer,
          timestamp: new Date(),
          tone: 'info',
        };
      } catch {
        return this.generateResponse('default');
      }
    } else if (promptId === 'qa-track-order') {
      const content = 'Open your invoice list to track order and payment status.';
      return {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        tone: 'info',
        link: { label: 'Go to invoice list', route: ['/operations/invoice-list'] },
      };
    } else if (promptId === 'qa-emergency-order') {
      const content = 'Opening your purchase orders filtered to sent status for urgent handling.';
      return {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content,
        timestamp: new Date(),
        tone: 'info',
        link: {
          label: 'Go to purchase orders',
          route: ['/operations/purchase-order-list'],
          queryParams: { selectedStatus: 'Sent' ,
            label:'Order'
          },
        },
      };
    } else {
      try {
        const role = this.getUserRole();
        const apiRes: any = await this.webApiService.postRequest('/ai/chat', {
          question: question || 'Help me with this.',
          role,
        });
        const answer =
          apiRes?.answer ||
          apiRes?.data?.answer ||
          apiRes?.data?.data?.answer ||
          this.scriptedResponses['default'];
        return {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: answer,
          timestamp: new Date(),
          tone: 'info',
        };
      } catch {
        return this.generateResponse('default');
      }
    }
    return Promise.resolve(this.generateResponse(promptId));
  }

  getUserRole(): string | undefined {
    const role = (this.cookieService.get('userRole') || '').toLowerCase();
    if (role === 'owner') return 'buyer';
    if (role === 'suppliers') return 'supplier';
    return role || undefined;
  }
}
